import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, MenuItem, Button, Chip, IconButton, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, FileDownload as DownloadIcon, Print as PrintIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { accountingAPI } from '../../services/api';
import { money, money2, label, errorText } from '../../utils/format';
import { fmtDate, localDate } from '../../utils/dates';
import { printPage, esc } from '../../utils/print';
import { PAYMENT_MODES, amountInWords } from './common';

const emptyForm = () => ({ category: '', title: '', description: '', amount: '', expenseDate: localDate(), paymentMode: 'CASH', vendorName: '', billNo: '', transactionId: '', paidBy: '', approvedBy: '', remarks: '' });

export default function ExpensesTab({ range, categories, notify, onChanged }) {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try { setAll((await accountingAPI.getExpenses()).data); }
    catch (e) { notify(errorText(e, 'Could not load expenses'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const rows = all.filter((e) => e.expenseDate >= range.from && e.expenseDate <= range.to)
    .filter((e) => cat === 'ALL' || e.category === cat)
    .filter((e) => !q || `${e.title} ${e.vendorName || ''} ${e.billNo || ''}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.expenseDate || '').localeCompare(a.expenseDate || ''));
  const total = rows.reduce((s, e) => s + Number(e.amount || 0), 0);
  const shown = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openDialog = (e = null) => {
    setEditing(e);
    setForm(e ? {
      category: e.category, title: e.title, description: e.description || '', amount: e.amount, expenseDate: e.expenseDate, paymentMode: e.paymentMode || 'CASH',
      vendorName: e.vendorName || '', billNo: e.billNo || '', transactionId: e.transactionId || '', paidBy: e.paidBy || '', approvedBy: e.approvedBy || '', remarks: e.remarks || '',
    } : { ...emptyForm(), category: categories[0] || '' });
    setOpen(true);
  };

  const save = async () => {
    try {
      const data = { ...form, amount: form.amount === '' ? null : Number(form.amount) };
      if (editing) await accountingAPI.updateExpense(editing.id, data); else await accountingAPI.createExpense(data);
      notify(editing ? 'Expense updated' : 'Expense recorded');
      setOpen(false);
      load();
      onChanged();
    } catch (e) { notify(errorText(e, 'Could not save the expense'), 'error'); }
  };

  const remove = async () => {
    try { await accountingAPI.deleteExpense(confirm.id); notify('Expense deleted'); setConfirm(null); load(); onChanged(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); }
  };

  const exportExcel = () => {
    const sheet = rows.map((e) => ({
      Date: e.expenseDate, Category: label(e.category), Title: e.title, Vendor: e.vendorName || '', 'Bill no': e.billNo || '', 'Amount (₹)': Number(e.amount),
      Mode: label(e.paymentMode), 'Transaction id': e.transactionId || '', 'Paid by': e.paidBy || '', 'Approved by': e.approvedBy || '', Remarks: e.remarks || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), 'Expenses');
    XLSX.writeFile(wb, `expenses_${range.from}_to_${range.to}.xlsx`);
  };

  const printVoucher = (e) => {
    const body = `<div style="max-width:640px;margin:10px auto;border:1px solid #ccc;border-radius:10px;padding:22px 26px">
      <div style="display:flex;justify-content:space-between;margin-bottom:14px"><b>Voucher for bill ${esc(e.billNo || '-')}</b><span>Date: ${esc(fmtDate(e.expenseDate))}</span></div>
      <table><tbody>
        <tr><td style="width:38%"><b>Paid to</b></td><td>${esc(e.vendorName || '-')}</td></tr>
        <tr><td><b>Towards</b></td><td>${esc(e.title)}${e.description ? ' - ' + esc(e.description) : ''}</td></tr>
        <tr><td><b>Category</b></td><td>${esc(label(e.category))}</td></tr>
        <tr><td><b>Payment mode</b></td><td>${esc(label(e.paymentMode))}${e.transactionId ? ' (' + esc(e.transactionId) + ')' : ''}</td></tr>
        <tr><td><b>Amount</b></td><td class="b" style="font-size:16px">${esc(money2(e.amount))}</td></tr>
        <tr><td><b>In words</b></td><td>${esc(amountInWords(e.amount))}</td></tr>
      </tbody></table>
      <div class="sig"><div>Prepared by<br/>${esc(e.paidBy || '')}</div><div>Approved by<br/>${esc(e.approvedBy || '')}</div><div>Receiver's signature</div></div></div>`;
    printPage('Payment Voucher', `Expense on ${fmtDate(e.expenseDate)}`, body);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search title, vendor, bill no..." value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Category" value={cat} onChange={(e) => { setCat(e.target.value); setPage(0); }} sx={{ minWidth: 220 }}>
            <MenuItem value="ALL">All categories</MenuItem>
            {[...new Set([...categories, ...all.map((e) => e.category)])].map((c) => <MenuItem key={c} value={c}>{label(c)}</MenuItem>)}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!rows.length}>Export Excel</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()}>Record expense</Button>
        </Box>
      </Box>
      <Chip sx={{ mb: 2, bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 }} label={`Total: ${money(total)} across ${rows.length} expense(s)`} />

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Date</TableCell><TableCell>Category</TableCell><TableCell>Title</TableCell><TableCell>Vendor</TableCell><TableCell>Bill no</TableCell>
              <TableCell>Mode</TableCell><TableCell align="right">Amount</TableCell><TableCell align="center">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {shown.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(e.expenseDate)}</TableCell>
                  <TableCell><Chip size="small" label={label(e.category)} /></TableCell>
                  <TableCell>{e.title}</TableCell>
                  <TableCell>{e.vendorName || '-'}</TableCell>
                  <TableCell>{e.billNo || '-'}</TableCell>
                  <TableCell>{label(e.paymentMode) || '-'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#ef6c00' }}>{money(e.amount)}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Print voucher"><IconButton size="small" onClick={() => printVoucher(e)}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                    <IconButton size="small" onClick={() => openDialog(e)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setConfirm(e)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>No expenses recorded for this period</TableCell></TableRow>}
            </TableBody>
          </Table>
          <TablePagination component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}><ReceiptIcon /> {editing ? 'Edit expense' : 'Record expense'}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {[...new Set([...categories, form.category].filter(Boolean))].map((c) => <MenuItem key={c} value={c}>{label(c)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={form.expenseDate} onChange={(e) => setForm({ ...form, expenseDate: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" multiline rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Amount (₹)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Payment mode" value={form.paymentMode} onChange={(e) => setForm({ ...form, paymentMode: e.target.value })}>
                {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{label(m)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Vendor name" value={form.vendorName} onChange={(e) => setForm({ ...form, vendorName: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Bill / invoice no" value={form.billNo} onChange={(e) => setForm({ ...form, billNo: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Transaction / cheque id" value={form.transactionId} onChange={(e) => setForm({ ...form, transactionId: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Paid by" value={form.paidBy} onChange={(e) => setForm({ ...form, paidBy: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Approved by" value={form.approvedBy} onChange={(e) => setForm({ ...form, approvedBy: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>{editing ? 'Update' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete expense?</DialogTitle>
        <DialogContent><Typography>{confirm?.title} ({money(confirm?.amount)}) will be removed from the books. This cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: 2.5 }}><Button onClick={() => setConfirm(null)}>Cancel</Button><Button color="error" variant="contained" onClick={remove}>Delete</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
